import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

const userDataStore = persist(
  (set) => ({
    currentUser: null,
    JwtToken: null,
    currentCompany: null,
    authStatus: false,
    showSettings: false,
    setCurrentUser: (userData) =>
      set(() => ({ currentUser: userData })),
    setJwtToken: (token) => set(() => ({ JwtToken: token })),
    setCurrentCompany: (data) =>
      set(() => ({ currentCompany: data })),
    setAuthStatus: (data) => set(() => ({ authStatus: data })),
    setShowSettings: (data) => set(() => ({ showSettings: data })),
  }),
  {
    name: "user-storage",
    storage: createJSONStorage(() => localStorage),
  }
);

const triggers = (set) => ({
  companyTrigger: false,
  apiKeyTrigger: false,
  rateSheetTrigger: false,
  tieredRateSheetTrigger: false,
  fuelSurchargetrigger: false,
  assetRateSheetTrigger: false,
  adminCompanyTrigger: false,
  companyMergeTrigger: false,
  setCompanyTrigger: (trigger) =>
    set(() => ({ companyTrigger: trigger })),
  setAdminCompanyTrigger: (trigger) =>
    set(() => ({ adminCompanyTrigger: trigger })),
  setApiKeyTrigger: (trigger) =>
    set(() => ({ apiKeyTrigger: trigger })),
  setRateSheetTrigger: (trigger) =>
    set(() => ({ rateSheetTrigger: trigger })),
  setTieredRateSheetTrigger: (trigger) =>
    set(() => ({ tieredRateSheetTrigger: trigger })),
  setFuelSurchargeTrigger: (trigger) =>
    set(() => ({ fuelSurchargeTrigger: trigger })),
  setAssetRateSheetTrigger: (trigger) =>
    set(() => ({ assetRateSheetTrigger: trigger })),
  setCompanyMergeTrigger: (trigger) =>
    set(() => ({ companyMergeTrigger: trigger })),
});

const messagesAndErrors = (set) => ({
  messageTrigger: false,
  pending: false,
  authError: null,
  error: null,
  successMessage: null,
  setMessageTrigger: (data) => set(() => ({ messageTrigger: data })),
  setError: (data) => set(() => ({ error: data })),
  setPending: (data) => () => ({ pending: data }),
  setAuthError: (data) => set(() => ({ authError: data })),
  setSuccessMessage: (data) => set(() => ({ successMessage: data })),
  toggleNewMessage: (errorMessage, successMessage) => {
    set({messageTrigger: true, error: errorMessage, successMessage});
    setTimeout(() => set({messageTrigger: false, error: null, successMessage: null}), 5000)
  }
});

const dbData = persist(
  (set) => ({
    typesData: null,
    companyData: null,
    globalCompanyNames: null,
    globalCompanies: null,
    rateSheetData: null,
    tieredRateSheets: null,
    fuelSurchargeData: null,
    assetRateSheets: null,
    projectData: [],
    users: null,
    companyLists: null,
    setTypesData: (data) => set(() => ({ typesData: data })),
    setCompanyData: (data) => set(() => ({ companyData: data })),
    setGlobalCompanyNames: (data) =>
      set(() => ({ globalCompanyNames: data })),
    setGlobalCompanies: (data) =>
      set(() => ({ globalCompanies: data })),
    setRateSheetData: (data) => set(() => ({ rateSheetData: data })),
    setTieredRateSheets: (data) => set(() => ({ tieredRateSheets: data })),
    setFuelSurchargeData: (data) =>
      set(() => ({ fuelSurchargeData: data })),
    setAssetRateSheets: (data) => set(() => ({ assetRateSheets: data })),
    setProjectData: (data) => set(() => ({ projectData: data })),
    setUsers: (data) => set(() => ({ users: data })),
    setCompanyLists: (data) => set(() => ({companyLists: data}))
  }),
  {
    name: "db-storage",
    storage: createJSONStorage(() => sessionStorage),
  }
);

const account = (set) => ({
  accountCustomerInvoices: null,
  accountCustomerInvoiceTotals: null,
  accountVendorInvoices: null,
  accountVendorInvoiceTotals: null,
  accountReceivedInvoices: null,
  accountReceivedInvoiceTotals: null,
  invoiceAdjustments: [],
  setAccountCustomerInvoices: (data) =>
    set(() => ({ accountCustomerInvoices: data })),
  setAccountCustomerInvoiceTotals: (data) =>
    set(() => ({ accountCustomerInvoiceTotals: data })),
  setAccountVendorInvoices: (data) =>
    set(() => ({ accountVendorInvoices: data })),
  setAccountVendorInvoiceTotals: (data) =>
    set(() => ({ accountVendorInvoiceTotals: data })),
  setAccountReceivedInvoices: (data) =>
    set(() => ({ accountReceivedInvoices: data })),
  setAccountReceivedInvoiceTotals: (data) =>
    set(() => ({ accountReceivedInvoiceTotals: data })),
  setInvoiceAdjustments: (data) =>
    set(() => ({ invoiceAdjustments: data })),
});

const project = (set) => ({
  projectLoads: null,
  projectAssetLoads: null,
  projectSandRates: null,
  projectCarriers: null,
  projectTotals: null,
  projectCreditsDebits: null,
  projectCustomerInvoiceTotals: null,
  projectVendorInvoiceTotals: null,
  projectAccruals: null,
  currentProject: null,
  projectCustomerInvoices: null,
  projectCustomerInvoiceLoads: null,
  projecVendorInvoices: null,
  projectUnaddedCarriers: [],
  projectRateSheets: [],
  setProjectLoads: (data) => set(() => ({ projectLoads: data })),
  setProjectAssetLoads: (data) =>
    set(() => ({ projectAssetLoads: data })),
  setProjectSandRates: (data) => set(() => ({ projectSandRates: data })),
  setProjectCarriers: (data) => set(() => ({ projectCarriers: data })),
  setProjectTotals: (data) => set(() => ({ projectTotals: data })),
  setProjectCreditsDebits: (data) =>
    set(() => ({ projectCreditsDebits: data })),
  setProjectCustomerInvoiceTotals: (data) =>
    set(() => ({ projectCustomerInvoiceTotals: data })),
  setProjectVendorInvoiceTotals: (data) =>
    set(() => ({ projectVendorInvoiceTotals: data })),
  setProjectAccruals: (data) => set(() => ({ projectAccruals: data })),
  setCurrentProject: (data) => set(() => ({ currentProject: data })),
  setProjectCustomerInvoices: (data) =>
    set(() => ({ projectCustomerInvoices: data })),
  setProjectVendorInvoices: (data) =>
    set(() => ({ projectVendorInvoices: data })),
  setProjectCustomerInvoiceLoads: (data) =>
    set(() => ({ projectCustomerInvoiceLoads: data })),
  setProjectUnaddedCarriers: (data) =>
    set(() => ({ projectUnaddedCarriers: data })),
  setProjectRateSheets: (data) =>
    set(() => ({ projectRateSheets: data })),
});

const company = (set) => ({
  companyCarriers: null,
  carrierDrivers: null,
  invoiceCounts: null,
  companyDrivers: null,
  setCompanyCarriers: (data) => set(() => ({ companyCarriers: data })),
  setCarrierDrivers: (data) => set(() => ({ carrierDrivers: data })),
  setInvoiceCounts: (data) => set(() => ({ invoiceCounts: data })),
  setCompanyDrivers: (data) => set(() => ({ companyDrivers: data })),
});


const selectOptions = persist(
  (set) => ({
    carrierOptions: null,
    terminalOptions: null,
    purchaseOrderOptions: null,
    setCarrierOptions: (data) => set(() => ({ carrierOptions: data })),
    setTerminalOptions: (data) => set(() => ({ terminalOptions: data })),
    setPurchaseOrderOptions: (data) =>
      set(() => ({ purchaseOrderOptions: data })),
  }),
  {
    name: "select-options-storage",
    storage: createJSONStorage(() => sessionStorage),
  }
);

const externalData = (set) => ({
  fscArea: null,
  projectDieselPrices: null,
  propConnectJobs: null,
  pcCarrierDrivers: null,
  setFscArea: (data) => set(() => ({ fscArea: data })),
  setProjectDieselPrices: (data) =>
    set(() => ({ projectDieselPrices: data })),
  setPropConnectJobs: (data) => set(() => ({ propConnectJobs: data })),
  setPcCarrierDrivers: (data) => set(() => ({ pcCarrierDrivers: data })),
});

const awsData = persist(
  (set) => ({
    profilePic: null,
    userProfiles: null,
    setProfilePic: (data) => set(() => ({ profilePic: data })),
    setUserProfiles: (data) => set(() => ({ userProfiles: data })),
  }),
  {
    name: "profile",
    storage: createJSONStorage(() => sessionStorage),
  }
);

const adminData = (set) => ({
  adminCompanyData: null,
  adminUsers: null,
  setAdminCompanyData: (data) => set(() => ({ adminCompanyData: data })),
  setAdminUsers: (data) => set(() => ({ adminUsers: data })),
});

export const dbDataStore = create(dbData);
export const projectStore = create(project);
export const triggersStore = create(triggers);
export const userStore = create(userDataStore);
export const messageAndErrorStore = create(messagesAndErrors);
export const externalStore = create(externalData);
export const awsDataStore = create(awsData);
export const selectOptionsStore = create(selectOptions);
export const adminDataStore = create(adminData);
export const accountDataStore = create(account);
export const companyStore = create(company);
